import { HStack, VStack } from "@/components/Layout";
import { AddStepperCompleteDialog } from "@/components/Stepper";
import Processing from "@/features/import-csv/-components/Processing";
import { defaultNS } from "@/lib/i18n";
import { LIST_EMPTY_DATA } from "@/lib/listPage";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import AddStepperNavigation from "../-components/AddStepperNavigation";
import AddStepperPanelProgress from "../-components/AddStepperPanelProgress";
import AddStepperStepTitleCard from "../-components/AddStepperStepTitleCard";
import BasicForm from "../-components/BasicForm";
import SelectPeopleGroup from "../-components/SelectPeopleGroup";
import SelectPermission from "../-components/SelectPermission";
import { ROLE_CREATE_HASH } from "../-constants";
import useBasicForm from "../-hooks/useBasicForm";
import useHash from "../-hooks/useHash";
import useSelectPeopleGroup from "../-hooks/useSelectPeopleGroup";
import useSelectPermission from "../-hooks/useSelectPermission";
import useCreateRole from "../-mutations/useCreateRole";
import useGetPeopleGroups from "../../../_list/people-groups/-queries/useGetPeopleGroups";
import useGetProductPermissions from "../../../_list/product-permissions/-queries/useGetProductPermissions";
import { TabKey } from "../../../_list/roles/-constants";
import useGetRoles from "../../../_list/roles/-queries/useGetRoles";

export const Route = createFileRoute(
  "/_staff/settings/permission-and-roles/_form/roles/new/",
)({
  component: RouteComponent,
});

const BASE_LINK = "/settings/permission-and-roles/roles/new";

function RouteComponent() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.settings.permissionAndRoles.createRole",
  });

  const { hash } = useHash();

  const { data: rolesRes } = useGetRoles({
    search: {
      category: TabKey.ALL,
      pageIndex: 0,
      pageSize: 100,
    },
  });

  const { basicFormState, handleChange, isDuplicateName } = useBasicForm({
    data: undefined,
    existingRoles: rolesRes ?? LIST_EMPTY_DATA,
  });

  const {
    selectPeopleGroupState,
    handleChangeOptions: handleChangeOptionsPeopleGroup,
    handleChangeSearchText: handleChangeSearchTextPeopleGroup,
    handleClearAll: handleClearAllPeopleGroup,
  } = useSelectPeopleGroup({
    data: undefined,
  });

  const {
    selectPermissionState,
    handleChangeOptions: handleChangeOptionsPermission,
    handleChangeSearchText: handleChangeSearchTextPermission,
    handleClearAll: handleClearAllPermission,
  } = useSelectPermission({
    data: undefined,
  });

  // for select people group（API分けても良いかも）
  const { data: peopleGroupsRes } = useGetPeopleGroups({
    search: {
      category: TabKey.ALL,
      pageIndex: 0,
      pageSize: 10,
      freeText: selectPeopleGroupState.searchText,
    },
  });

  // for select permission（API分けても良いかも）
  const { data: productPermissionsRes } = useGetProductPermissions({
    search: {
      category: TabKey.ALL,
      pageIndex: 0,
      pageSize: 10,
      freeText: selectPermissionState.searchText,
    },
  });

  const { mutate: createRole, isPending, isComplete } = useCreateRole();

  return (
    <HStack className="gap-9 p-10 h-[calc(100vh-64px)]">
      <AddStepperNavigation baseLink={BASE_LINK} currentHash={hash} />

      <VStack className="gap-6 w-full">
        {isPending && <Processing hash={hash} />}

        {!isPending && <AddStepperStepTitleCard hash={hash} />}

        {!isPending && hash === ROLE_CREATE_HASH.FORM && (
          <BasicForm
            basicFormState={basicFormState}
            handleChange={handleChange}
            isDuplicateName={isDuplicateName}
          />
        )}

        {!isPending && hash === ROLE_CREATE_HASH.SELECT_PEOPLE_GROUP && (
          <SelectPeopleGroup
            selectPeopleGroupState={selectPeopleGroupState}
            handleChangeOptions={handleChangeOptionsPeopleGroup}
            handleChangeSearchText={handleChangeSearchTextPeopleGroup}
            handleClearAll={handleClearAllPeopleGroup}
            peopleGroups={peopleGroupsRes?.items ?? []}
          />
        )}

        {!isPending && hash === ROLE_CREATE_HASH.SET_PERMISSION && (
          <SelectPermission
            selectPermissionState={selectPermissionState}
            handleChangeOptions={handleChangeOptionsPermission}
            handleChangeSearchText={handleChangeSearchTextPermission}
            handleClearAll={handleClearAllPermission}
            permissions={productPermissionsRes?.items ?? []}
          />
        )}

        {!isPending && (
          <AddStepperPanelProgress
            baseLink={BASE_LINK}
            currentHash={hash}
            basicFormState={basicFormState}
            selectPeopleGroupState={selectPeopleGroupState}
            selectPermissionState={selectPermissionState}
            mutate={createRole}
            isDuplicateName={isDuplicateName}
          />
        )}

        {isComplete && (
          <AddStepperCompleteDialog
            completeMessage={t(
              "addStepperCompleteDialog.create.completeMessage",
            )}
            completeLinkButtonText={t(
              "addStepperCompleteDialog.create.completeLinkButtonText",
            )}
            completeLinkUrl="/settings/permission-and-roles/roles"
          />
        )}
      </VStack>
    </HStack>
  );
}
